// WP13 Market Zone detail — complete authoritative Commit-6 view.
// zoneDefinition is consumed from the package when hydrated; while absent
// the Scope section is omitted (WP11_ZONE_DEFINITION_HYDRATION_GAP reported
// upstream — never hard-coded in the frontend).
// No zone weekly score timeline is published (UPSTREAM GAP) — evidence
// counts are never substituted as a structural trend.
import React from "react";
import { Link, useSearchParams } from "react-router-dom";
import { useWp12 } from "./useWp12";
import { MarketLayout } from "./MarketTree";
import { StateGrid } from "./StateGrid";
import { RegionMiniMap } from "./RegionMiniMap";
import InfoTip from "./InfoTip";
import { StateChip, patternDisplayTitle } from "./TopPatternsTable";
import { DeepInsightDot } from "./DeepInsight";
import { zoneColor } from "./LeadersChart";
import { Sparkline } from "./charts";

const enumLabel = (v) => (typeof v === "string" ? v.replace(/_/g, " ").toLowerCase() : "—");

export default function ZoneBrief() {
    const [params] = useSearchParams();
    const scopeKey = params.get("zone") || "";
    const { status, data: market, error } = useWp12("market");

    if (status === "loading") return <div className="p-8 text-sm text-[hsl(var(--mute))]" data-testid="wp13-zone-loading">Loading…</div>;
    const zone = (market?.marketZones || []).find((z) => z.scopeKey === scopeKey);
    if (!zone) {
        return (
            <div className="p-8 space-y-2" data-testid="wp13-zone-notfound">
                <p className="text-sm">Market Zone not found in the active publication package{error ? ` (${error})` : ""}.</p>
                <Link to="/market-v2/market" className="text-xs text-[hsl(var(--accent-ink))] hover:underline">← Back to Market</Link>
            </div>
        );
    }

    const brief = zone.structuralBrief || {};
    const color = zoneColor(zone.presentationColorToken);
    const h4 = (zone.horizons || []).find((h) => h.horizonWeeks === 4);
    const h12 = (zone.horizons || []).find((h) => h.horizonWeeks === 12);
    const zoneUseCases = (market.useCases || []).filter((u) => (u.marketZoneKeys || []).includes(scopeKey));
    const zonePatterns = (market.patternCards || [])
        .filter((c) => c.marketZoneKey === scopeKey)
        .sort((a, b) => (b.strength?.displayScoreExact ?? -1) - (a.strength?.displayScoreExact ?? -1));
    const totalPatterns = zone.counts?.patternCount ?? zonePatterns.length;

    return (
        <MarketLayout market={market} selectedZoneKey={scopeKey}>
            <div className="space-y-8" data-testid="wp13-zone-brief">
                {/* ── Header with explicit state labels ──────────────────────── */}
                <div className="border-l-4 pl-4" style={{ borderColor: color }}>
                    <div className="eyebrow">Market Zone</div>
                    <h1 className="text-xl font-semibold mt-0.5" data-testid="wp13-zone-title">{zone.label}</h1>
                    <div className="flex items-baseline gap-5 flex-wrap mt-2">
                        <span data-testid="wp13-zone-score">
                            <span className="num text-xl font-semibold">{zone.strength?.displayScoreRounded ?? "—"}</span>
                            <span className="text-[10px] text-[hsl(var(--mute))] ml-1.5">{enumLabel(zone.strength?.scoreBand)}</span>
                        </span>
                        <span className="num text-[10px] text-[hsl(var(--mute))]">
                            {totalPatterns} published patterns · {zone.counts?.useCaseCount ?? zoneUseCases.length} use cases
                        </span>
                    </div>
                    <div className="mt-3">
                        <StateGrid testId="wp13-zone-state" items={[
                            { label: "4w trend", value: h4?.trend?.primaryTrendClass },
                            { label: "12w trend", value: h12?.trend?.primaryTrendClass },
                            { label: "Velocity", value: h12?.trend?.velocityState },
                            { label: "Breadth", value: h12?.breadth?.actorTrendClass },
                            { label: "Persistence", value: h12?.trend?.persistenceState },
                            { label: "Reliability", value: h12?.trend?.reliability, plain: true },
                        ]} />
                    </div>
                </div>

                {/* ── Scope (authoritative zoneDefinition when hydrated) ──────── */}
                {(zone.zoneDefinition || brief.zoneDefinition) && (
                    <section className="space-y-2" data-testid="wp13-zone-definition">
                        <h2 className="text-sm font-semibold">Scope</h2>
                        <p className="text-[12.5px] leading-relaxed card-flat p-4">{zone.zoneDefinition || brief.zoneDefinition}</p>
                    </section>
                )}

                {/* ── Structural view ─────────────────────────────────────────── */}
                {brief.structuralStateBrief && (
                    <section className="space-y-2" data-testid="wp13-zone-full-brief">
                        <h2 className="text-sm font-semibold">Structural view</h2>
                        <div className="card-flat p-5"><p className="text-[12.5px] leading-relaxed">{brief.structuralStateBrief}</p></div>
                    </section>
                )}

                {/* ── Geography + drivers side by side ───────────────────────── */}
                <div className="grid grid-cols-1 xl:grid-cols-2 gap-5 items-start">
                    {(zone.geographyRefs || []).length > 0 && (
                        <section className="space-y-2" data-testid="wp13-zone-geography">
                            <h2 className="text-sm font-semibold inline-flex items-center gap-2">
                                Region-linked observed activity
                                <InfoTip label="Zone geography" testId="wp13-zone-tip-geo">Published association between this Market Zone and regions where supporting activity was observed — presence only, not a regional score or share.</InfoTip>
                            </h2>
                            <div className="card-flat p-3"><RegionMiniMap geoKeys={zone.geographyRefs} color={color} /></div>
                        </section>
                    )}
                    {(brief.keyStructuralDrivers || []).length > 0 && (
                        <section className="space-y-2" data-testid="wp13-zone-drivers">
                            <h2 className="text-sm font-semibold">Key structural drivers</h2>
                            <ul className="card-flat p-4 space-y-2 list-disc pl-8">
                                {brief.keyStructuralDrivers.map((d) => (
                                    <li key={d.driverKey} className="text-[11.5px] leading-relaxed">{d.text}</li>
                                ))}
                            </ul>
                        </section>
                    )}
                </div>

                {/* ── Use cases (clickable canonical routes) ──────────────────── */}
                {zoneUseCases.length > 0 && (
                    <section className="space-y-2" data-testid="wp13-zone-usecases">
                        <h2 className="text-sm font-semibold">Use cases</h2>
                        <div className="flex items-center gap-2 flex-wrap">
                            {zoneUseCases.map((u) => (
                                <Link key={u.scopeKey}
                                      to={`/market-v2/market/use-cases/${encodeURIComponent(u.scopeKey)}`}
                                      data-testid={`wp13-zone-usecase-${u.scopeKey}`}
                                      className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full border text-[11px] hover:bg-[hsl(var(--surface-sunken))] hover:underline"
                                      style={{ borderColor: color.replace(")", " / 0.45)") }}>
                                    <span className="w-1.5 h-1.5 rounded-full" style={{ background: color }} />
                                    {u.label}
                                    <span className="num text-[9px] text-[hsl(var(--mute))]">{u.counts?.patternCount}</span>
                                </Link>
                            ))}
                        </div>
                    </section>
                )}

                {/* ── Strongest patterns ──────────────────────────────────────── */}
                <section className="space-y-2" data-testid="wp13-zone-patterns">
                    <h2 className="text-sm font-semibold">Strongest patterns in this zone</h2>
                    <div className="card-flat divide-y divide-[hsl(var(--line))] border-l-2" style={{ borderLeftColor: color.replace(")", " / 0.5)") }}>
                        {zonePatterns.slice(0, 8).map((c) => (
                            <Link key={c.canonicalPatternId}
                                  to={`/market-v2/patterns/${encodeURIComponent(c.canonicalPatternId)}`}
                                  data-testid={`wp13-zone-pattern-${c.canonicalPatternId}`}
                                  className="px-5 py-2.5 flex items-center gap-4 hover:bg-[hsl(var(--surface-sunken))] group">
                                <div className="min-w-0 flex-1">
                                    <div className="flex items-center gap-1.5 min-w-0">
                                        <span className="text-[11.5px] font-medium truncate group-hover:text-[hsl(var(--accent-ink))]">{patternDisplayTitle(c)}</span>
                                        {c.hasDurableInterpretation && <DeepInsightDot testId={`wp13-zone-di-${c.canonicalPatternId}`} />}
                                    </div>
                                    <div className="text-[9px] text-[hsl(var(--mute))] truncate">{c.useCaseLabel || ""}</div>
                                </div>
                                {(c.scoreTimeline12w || []).length > 1 && (
                                    <Sparkline values={c.scoreTimeline12w.map((pt) => pt.displaySignalScoreExact)} width={70} height={18} stroke={color} />
                                )}
                                <div className="w-24 flex-shrink-0"><StateChip value={c.horizonSummary?.h12?.trend?.primaryTrendClass} /></div>
                                <span className="num text-[11px] font-semibold w-10 text-right">{c.strength?.displayScoreExact?.toFixed(1) ?? "—"}</span>
                            </Link>
                        ))}
                    </div>
                    <p className="text-[9px] text-[hsl(var(--mute))]" data-testid="wp13-zone-pattern-count">
                        Showing the {Math.min(8, zonePatterns.length)} strongest of {totalPatterns} published patterns in this zone.
                    </p>
                </section>

                {/* ── Published claims — secondary / expandable ───────────────── */}
                {(brief.claims || []).length > 0 && (
                    <details className="card-flat" data-testid="wp13-zone-claims">
                        <summary className="px-5 py-3 text-sm font-semibold cursor-pointer select-none">
                            Published claims & methodology detail
                            <span className="num text-[9px] text-[hsl(var(--mute))] ml-2">{brief.claims.length}</span>
                        </summary>
                        <div className="divide-y divide-[hsl(var(--line))] border-t border-[hsl(var(--line))]">
                            {brief.claims.map((c) => (
                                <div key={c.claimId} className="px-5 py-3 flex items-baseline gap-3" data-testid={`wp13-zone-claim-${c.claimId}`}>
                                    <span className="text-[9px] uppercase tracking-wider text-[hsl(var(--mute))] flex-shrink-0 w-24">{enumLabel(c.claimFacet)}</span>
                                    <p className="text-[11.5px] leading-relaxed">{c.statement}</p>
                                </div>
                            ))}
                        </div>
                    </details>
                )}
            </div>
        </MarketLayout>
    );
}
